import React, { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import { ContextActionType } from '@europrocurement/flexy-components/providers';
import {
    SidebarWidth,
    SidebarFoldedWidth,
    paddingContent,
} from '../../../assets/global/Theme-variable';
import { type MenuEntry, HeaderProps, FooterProps, SidebarProps, Footer } from '../../organisms';
import { Scrollbar, ScrollbarHandle } from '../../molecules';
import ScrollbarContext from '../../../providers/Scrollbar/ScrollbarContext';
import { useFullLayout } from './FullLayoutContext';

export type FullLayoutProps = {
    menuEntries: MenuEntry[];
    footerProps?: FooterProps;
    headerProps?: HeaderProps;
    contextActionMainIcon?: ReactNode;
    defaultContextActions?: ContextActionType[];
    logo: SidebarProps['logo'];
    logoFolded: SidebarProps['logoFolded'];
    sidebarActions?: SidebarProps['actions'];
    pageTitle?: string;
};

export type FullLayoutOutletProps = {
    footerProps?: FooterProps;
    source?: 'BACK' | 'FRONT';
};

export const FullLayoutOutlet: React.FunctionComponent<FullLayoutOutletProps> = function ({
    footerProps,
    source,
}) {
    const { breakpoints } = useTheme();

    const smBreakpoint = useMediaQuery(breakpoints.up('sm'));
    const hiddenSidebarContentPaddingLeft = smBreakpoint
        ? SidebarFoldedWidth + paddingContent
        : paddingContent;

    const { isSidebarOpen } = useFullLayout();

    const scrollbarRef = React.useRef<ScrollbarHandle>(null);

    const scrollbarContextValue = React.useMemo(
        () => ({
            scrollToTop: () => scrollbarRef.current?.scrollToTop(),
        }),
        [],
    );

    return (
        <ScrollbarContext.Provider value={scrollbarContextValue}>
            <Scrollbar ref={scrollbarRef}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: source === 'BACK' ? 'calc(100vh - 72px)' : '110vh',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            paddingLeft: isSidebarOpen
                                ? `${SidebarWidth + paddingContent}px !important`
                                : `${hiddenSidebarContentPaddingLeft}px !important`,
                            paddingRight: `${paddingContent}px !important`,
                            zIndex: 2,
                        }}
                    >
                        <Outlet />
                    </Box>
                    <Footer {...footerProps} />
                </Box>
            </Scrollbar>
        </ScrollbarContext.Provider>
    );
};
